import { memo, useCallback, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import {
    Badge,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import {
    PuppetAnimationKey,
    PuppetAnimationsArray,
    InterviewLanguage,
} from '@synergetx/puppet-interview-types';
import { toast } from 'react-toastify';

import LanguageSelectAutocomplete from './CreateSurvey/LanguageSelectAutocomplete';
import GeneralAudioUploadButtons from './GeneralAudioUploadButtons';
import Puppets from './Puppets';
import { InterviewEdited } from './types';
import { saveInterview } from '@modules/firebase/interviews';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 2, minHeight: 300 }}>
        {value === index && children}
    </Box>
);

// Custom hook to check for errors in settings fields
const useSettingsErrors = () => {
    const { errors } = useFormikContext<InterviewEdited>();
    const [{ value: languages }] = useField<InterviewLanguage[]>('languages');

    const hasLanguagesError = useMemo(() => {
        return !!errors.languages || languages.length === 0;
    }, [errors.languages, languages.length]);

    const hasAnimationError = useMemo(() => {
        return !!errors.puppetAnimation;
    }, [errors.puppetAnimation]);

    const hasAudioError = useMemo(() => {
        return (
            !!errors.interviewGeneralAudio &&
            Object.keys(errors.interviewGeneralAudio as object).length > 0
        );
    }, [errors.interviewGeneralAudio]);

    const totalErrors = useMemo(() => {
        let count = 0;
        if (hasLanguagesError) count++;
        if (hasAnimationError) count++;
        if (hasAudioError) {
            // Count audio errors per language
            const audioErrors = errors.interviewGeneralAudio as Record<string, unknown> | undefined;
            if (audioErrors) {
                count += Object.keys(audioErrors).length;
            }
        }
        return count;
    }, [hasLanguagesError, hasAnimationError, hasAudioError, errors.interviewGeneralAudio]);

    const hasAnyError = hasLanguagesError || hasAnimationError || hasAudioError;

    return {
        hasLanguagesError,
        hasAnimationError,
        hasAudioError,
        hasAnyError,
        totalErrors,
    };
};

// Tab label with error indicator
interface TabLabelProps {
    label: string;
    hasError: boolean;
}

const TabLabel = memo(({ label, hasError }: TabLabelProps) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {hasError && <ErrorOutlineIcon color="error" sx={{ fontSize: 16, ml: 0.5 }} />}
    </Box>
));
TabLabel.displayName = 'TabLabel';

const SurveySettingsModal = () => {
    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const { hasLanguagesError, hasAnimationError, hasAudioError, hasAnyError, totalErrors } =
        useSettingsErrors();

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Auto-navigate to first tab with error when opening
    const handleOpenWithErrorFocus = useCallback(() => {
        setOpen(true);
        if (hasLanguagesError) {
            setTabValue(0);
        } else if (hasAnimationError) {
            setTabValue(1);
        } else if (hasAudioError) {
            setTabValue(2);
        }
    }, [hasLanguagesError, hasAnimationError, hasAudioError]);

    const tooltipTitle = hasAnyError
        ? `Survey Settings (${totalErrors} issue${totalErrors > 1 ? 's' : ''} need attention)`
        : 'Survey Settings';

    return (
        <>
            <Tooltip title={tooltipTitle}>
                <IconButton
                    color={hasAnyError ? 'error' : 'info'}
                    onClick={hasAnyError ? handleOpenWithErrorFocus : handleOpen}
                    sx={{ ml: 1 }}
                >
                    <Badge
                        color="error"
                        variant="dot"
                        invisible={!hasAnyError}
                        sx={{
                            '& .MuiBadge-badge': {
                                right: 2,
                                top: 2,
                            },
                        }}
                    >
                        <SettingsOutlinedIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: { sx: { minHeight: '60vh' } },
                }}
            >
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">Survey Settings</Typography>
                        {hasAnyError && (
                            <Typography variant="caption" color="error">
                                ({totalErrors} issue{totalErrors > 1 ? 's' : ''})
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                minHeight: 48,
                            },
                        }}
                    >
                        <Tab label={<TabLabel label="Languages" hasError={hasLanguagesError} />} />
                        <Tab label={<TabLabel label="Animation" hasError={hasAnimationError} />} />
                        <Tab label={<TabLabel label="Audio Files" hasError={hasAudioError} />} />
                    </Tabs>
                </Box>
                <DialogContent>
                    <TabPanel value={tabValue} index={0}>
                        <LanguagesSection />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        <AnimationSection />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                        <AudioSection />
                    </TabPanel>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                    <ArchiveButton onClose={handleClose} />
                    <Button variant="contained" color="info" onClick={handleClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Languages Tab
const LanguagesSection = memo(() => {
    const { errors } = useFormikContext<InterviewEdited>();
    const [{ value: languages }] = useField<InterviewLanguage[]>('languages');
    const hasError = !!errors.languages || languages.length === 0;

    return (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    mb: 2,
                }}
            >
                Select which languages this survey will support. At least one language is required.
            </Typography>
            <LanguageSelectAutocomplete />
            {hasError && languages.length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    At least one language is required
                </Typography>
            )}
        </Box>
    );
});
LanguagesSection.displayName = 'LanguagesSection';

// Animation Tab
const AnimationSection = memo(() => {
    const [{ value: puppetAnimation }, { error }, { setValue: setPuppetAnimation }] =
        useField<PuppetAnimationKey>('puppetAnimation');

    const handleChange = useCallback(
        (_: React.MouseEvent<HTMLElement>, newPuppet: PuppetAnimationKey) => {
            if (newPuppet !== null) {
                setPuppetAnimation(newPuppet);
                toast.success('Animation updated!');
            }
        },
        [setPuppetAnimation],
    );

    return (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    mb: 2,
                }}
            >
                Choose the puppet animation style for puppet questions.
            </Typography>
            <ToggleButtonGroup
                color={error ? 'error' : 'primary'}
                value={puppetAnimation}
                exclusive
                onChange={handleChange}
                aria-label="Puppet Animation"
                sx={{ mb: 3 }}
            >
                {PuppetAnimationsArray.map((puppet) => (
                    <ToggleButton key={puppet} value={puppet}>
                        {puppet}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{
                        display: 'block',
                        mb: 2,
                    }}
                >
                    {error}
                </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography
                variant="subtitle2"
                sx={{
                    mb: 2,
                }}
            >
                Preview
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    gap: 2,
                }}
            >
                {Puppets[puppetAnimation]?.still && (
                    <Box
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        <img
                            src={Puppets[puppetAnimation].still}
                            width="150"
                            height="auto"
                            alt="Still puppet"
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                            }}
                        >
                            Still
                        </Typography>
                    </Box>
                )}
                {Puppets[puppetAnimation]?.talking && (
                    <Box
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        <img
                            src={Puppets[puppetAnimation].talking}
                            width="150"
                            height="auto"
                            alt="Talking puppet"
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                            }}
                        >
                            Talking
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});
AnimationSection.displayName = 'AnimationSection';

// Audio Tab
const AudioSection = memo(() => {
    const { errors } = useFormikContext<InterviewEdited>();
    const hasAudioErrors =
        !!errors.interviewGeneralAudio &&
        Object.keys(errors.interviewGeneralAudio as object).length > 0;

    return (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    mb: 2,
                }}
            >
                Upload general audio files for each language. These are used for common responses.
            </Typography>
            {hasAudioErrors && (
                <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
                    Some audio files are missing. Please upload all required audio for each
                    language.
                </Typography>
            )}
            <GeneralAudioUploadButtons />
        </Box>
    );
});
AudioSection.displayName = 'AudioSection';

// Archive Button in dialog actions
const ArchiveButton = memo(({ onClose }: { onClose: () => void }) => {
    const [{ value: archived }] = useField<boolean | undefined>('archived');
    const { values } = useFormikContext<InterviewEdited>();

    const tooltipTitle = useMemo(
        () =>
            values.isEditingLocked
                ? 'Cannot archive while survey is locked'
                : archived
                  ? 'Activate the survey and make it available to users'
                  : 'Archive survey to hide it from the mobile app',
        [archived, values.isEditingLocked],
    );

    const confirmMessage = useMemo(
        () =>
            archived
                ? 'Activate the survey and make it available to users? This will allow assessors to see it again in the mobile app.'
                : 'Are you sure you want to archive this survey? Assessors will no longer see it in the mobile app.',
        [archived],
    );

    const handleChangeArchived = useCallback(async () => {
        if (window.confirm(confirmMessage)) {
            await saveInterview(values.groupId, { ...values, archived: !archived }).then(() => {
                onClose();
                window.location.reload();
            });
        }
    }, [archived, confirmMessage, values, onClose]);

    return (
        <Tooltip title={tooltipTitle}>
            <span>
                <Button
                    variant="outlined"
                    color={archived ? 'info' : 'error'}
                    disabled={values.isEditingLocked}
                    onClick={handleChangeArchived}
                    startIcon={archived ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />}
                >
                    {archived ? 'Unarchive' : 'Archive'}
                </Button>
            </span>
        </Tooltip>
    );
});
ArchiveButton.displayName = 'ArchiveButton';

export default memo(SurveySettingsModal);
